// File project, the project's files panel (design review ui.region_gate
// project_page section_container: rows, relevance 0.84). Head: the h2 and
// Unggah file. Then one row per file: the type glyph, the name (one line,
// ellipsis), the kind badge (Diunggah, Dibuat Pluto, Drive) with size and
// date, and Unduh in its own 44px lane for a file that has an attachment. A
// file found only in the project's Drive folder has no download route and
// says Tersimpan di Google Drive instead.
// Unggah file checks the batch first (PNG, JPG, WEBP, PDF, DOCX, XLSX, TXT;
// 10 MB each; 5 at a time), then sends it with the project id. Storage that
// is not connected (driveConnected false, or a 503) is one inline notice and
// Unggah file stays off.
import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import * as API from "../api";
import type { ProjectFile, ProjectFiles as ProjectFilesData } from "../api";
import { formatDate } from "../lib/format";
import { useChatShell } from "../shell/chat-context";
import { Badge, Notice, useAsync, useDelayed } from "../ui/components";
import { Glyph } from "../ui/icons";
import { checkProjectFiles, DRIVE_OFF_TEXT, DRIVE_ONLY_HINT, fileGlyph, formatFileSize, KIND_LABEL, PROJECT_FILE_ACCEPT, type ProjectFileKind } from "./files";

export interface ProjectFilesPanelProps {
  projectId: string;
  /** The id of the panel's h2 (two panels never share a page). */
  headingId?: string;
}

function isDriveOff(err: unknown): boolean {
  return err instanceof API.ApiError && err.status === 503;
}

export function ProjectFilesPanel({ projectId, headingId = "pj-files-title" }: ProjectFilesPanelProps) {
  const shell = useChatShell();
  const tick = shell?.historyTick ?? 0;
  const [uploaded, setUploaded] = useState(0);
  const files = useAsync<ProjectFilesData>(() => API.listProjectFiles(projectId), [projectId, tick, uploaded]);
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [driveDown, setDriveDown] = useState(false);
  const shownSkeleton = useDelayed(files.loading && !files.data);

  const driveOff = driveDown || files.data?.driveConnected === false || isDriveOff(files.error);

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.currentTarget.files ?? []);
    // The same file can be picked again after a refusal.
    e.currentTarget.value = "";
    if (picked.length === 0) return;
    setDone(null);
    const problem = checkProjectFiles(picked);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const refs = await API.uploadAttachments(picked, projectId);
      setDone(refs.length === 1 ? "1 file diunggah." : `${refs.length} file diunggah.`);
      setUploaded((n) => n + 1);
    } catch (err) {
      if (isDriveOff(err)) setDriveDown(true);
      else setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  let body: ReactNode;
  if (files.loading && !files.data) {
    body = (
      <div className="pj-rows-sk" role="status" aria-busy="true" aria-label="Memuat file project">
        {shownSkeleton ? Array.from({ length: 3 }, (_, i) => <span key={i} className="sk pj-row-sk" aria-hidden="true" />) : null}
      </div>
    );
  } else if (files.error && !isDriveOff(files.error)) {
    body = (
      <Notice
        tone="danger"
        title="File project belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={files.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(files.error)}</p>
      </Notice>
    );
  } else {
    const rows = files.data?.files ?? [];
    body =
      rows.length === 0 ? (
        <p className="pj-empty">{driveOff ? "Belum ada file di project ini." : "Belum ada file. File yang Anda unggah atau yang disusun di chat project ini tampil di sini."}</p>
      ) : (
        <ul className="pj-rows" aria-labelledby={headingId}>
          {rows.map((f) => (
            <FileRow key={f.attachmentId ?? `drive-${f.driveFileId}`} file={f} />
          ))}
        </ul>
      );
  }

  return (
    <section className="pj-panel pj-files" aria-labelledby={headingId}>
      <div className="pj-panel-head">
        <h2 id={headingId} className="pj-panel-title">
          File project
        </h2>
        <input ref={picker} type="file" multiple accept={PROJECT_FILE_ACCEPT} hidden onChange={onPick} aria-label="Pilih file untuk diunggah" />
        <button type="button" className="btn btn-secondary pj-upload" onClick={() => picker.current?.click()} disabled={busy || driveOff} aria-busy={busy || undefined}>
          <Glyph name="add" size={18} />
          <span>{busy ? "Mengunggah" : "Unggah file"}</span>
        </button>
      </div>
      {driveOff ? (
        <p className="pj-drive-off" role="status">
          <Glyph name="infoCircle" size={16} />
          <span>{DRIVE_OFF_TEXT}</span>
        </p>
      ) : null}
      {error ? (
        <Notice tone="danger" title="File belum terunggah" className="pj-upload-error">
          <p>{error}</p>
        </Notice>
      ) : null}
      {done ? (
        <p className="pj-done" role="status">
          {done}
        </p>
      ) : null}
      {body}
    </section>
  );
}

function FileRow({ file }: { file: ProjectFile }) {
  const kind = file.kind as ProjectFileKind;
  const size = formatFileSize(file.size);
  const meta = [size, formatDate(file.createdAt)].filter(Boolean).join(", ");
  return (
    <li className="pj-row pj-file" data-kind={kind}>
      <span className="pj-row-icon" aria-hidden="true">
        <Glyph name={fileGlyph(file.mimeType, file.name)} size={20} />
      </span>
      <span className="pj-row-text">
        <span className="pj-row-name" title={file.name}>
          {file.name}
        </span>
        <span className="pj-row-meta">
          <Badge tone={kind === "generated" ? "info" : "neutral"}>{KIND_LABEL[kind] ?? KIND_LABEL.upload}</Badge>
          <span className="pj-row-detail num">{meta}</span>
        </span>
        {file.attachmentId ? null : <span className="pj-row-hint">{DRIVE_ONLY_HINT}</span>}
      </span>
      <span className="pj-row-lane">
        {file.attachmentId ? (
          <a className="ui-icon-btn pj-download" href={API.attachmentDownloadUrl(file.attachmentId)} download={file.name} aria-label={`Unduh ${file.name}`} title="Unduh">
            <Glyph name="documentDownload" />
          </a>
        ) : null}
      </span>
    </li>
  );
}
